import React, { useState, useEffect, useCallback } from 'react';
import {
    getAuditLogs,
    getAuditLogActions,
    getPlatformStats,
    getPerformanceStats,
    AuditLogEntry,
    AuditLogParams,
} from '../../services/api';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassInput } from '../ui/GlassInput';
import { GlassSelect } from '../ui/GlassSelect';
import { GlassBadge } from '../ui/GlassBadge';
import { GlassTabs } from '../ui/GlassTabs';
import { Download, RefreshCw, ChevronLeft, ChevronRight, Users, Megaphone, Image, TrendingUp, FileText, Database, Zap, Shield } from 'lucide-react';

const PAGE_SIZE = 50;

// ── Helpers ───────────────────────────────────────────────────────────────────

function actionVariant(action: string | null): 'success' | 'warning' | 'danger' | 'info' | 'default' {
    if (!action) return 'default';
    if (action.includes('deleted') || action.includes('failed')) return 'danger';
    if (action.includes('login') || action.includes('register')) return 'info';
    if (action.includes('created') || action.includes('published')) return 'success';
    if (action.includes('generated') || action.includes('captured')) return 'warning';
    return 'default';
}

function formatDate(iso: string | null): string {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}

function downloadCsv(logs: AuditLogEntry[], filename = 'audit_log.csv') {
    const headers = ['timestamp', 'user_email', 'action', 'resource_type', 'resource_name', 'resource_id', 'ip_address', 'success'];
    const rows = logs.map(l => [
        l.created_at ?? '',
        l.user_email ?? '',
        l.action ?? '',
        l.resource_type ?? '',
        l.resource_name ?? '',
        l.resource_id ?? '',
        l.ip_address ?? '',
        String(l.success),
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

// ── Platform Stats sub-component ─────────────────────────────────────────────

interface StatsData {
    users: { total: number; this_month: number };
    campaigns: { total: number };
    assets: { total: number };
    leads: { total: number };
    content: { total: number };
}

const PlatformStats: React.FC = () => {
    const [stats, setStats] = useState<StatsData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setLoading(true);
        getPlatformStats()
            .then(setStats)
            .catch(() => setError('Failed to load stats'))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div style={{ color: 'var(--text-secondary)', padding: 'var(--space-4)' }}>Loading stats…</div>;
    if (error || !stats) return <div style={{ color: 'var(--danger)', padding: 'var(--space-4)' }}>{error ?? 'No data'}</div>;

    const statCards = [
        { label: 'Total Users', value: stats.users.total, sub: `+${stats.users.this_month} this month`, icon: <Users size={20} /> },
        { label: 'Campaigns', value: stats.campaigns.total, icon: <Megaphone size={20} /> },
        { label: 'Assets Generated', value: stats.assets.total, icon: <Image size={20} /> },
        { label: 'Leads Captured', value: stats.leads.total, icon: <TrendingUp size={20} /> },
        { label: 'Content Pieces', value: stats.content.total, icon: <FileText size={20} /> },
    ];

    return (
        <div className="audit-stats-grid">
            {statCards.map(card => (
                <GlassCard key={card.label} variant="default" padding="md" className="audit-stat-card">
                    <div className="audit-stat-icon">{card.icon}</div>
                    <div className="audit-stat-value">{card.value.toLocaleString()}</div>
                    <div className="audit-stat-label">{card.label}</div>
                    {card.sub && <div className="audit-stat-sub">{card.sub}</div>}
                </GlassCard>
            ))}
        </div>
    );
};

// ── Performance Panel sub-component ──────────────────────────────────────────

interface CacheStats {
    size: number;
    valid_entries: number;
    maxsize: number;
    ttl_seconds: number;
}

interface PerformanceData {
    cache_stats: {
        analytics: CacheStats;
        seo: CacheStats;
        competitor: CacheStats;
    };
    database: {
        pool_size: number | string;
        checked_out: number | string;
    };
    rate_limiter: {
        tracked_ips: number;
        window_seconds: number;
        limit: number;
    };
}

const PerformancePanel: React.FC = () => {
    const [data, setData] = useState<PerformanceData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        getPerformanceStats()
            .then(setData)
            .catch(() => setError('Failed to load performance stats'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading) return <div style={{ color: 'var(--text-secondary)', padding: 'var(--space-4)' }}>Loading performance stats…</div>;
    if (error || !data) return <div style={{ color: 'var(--danger)', padding: 'var(--space-4)' }}>{error ?? 'No data'}</div>;

    const caches = [
        { key: 'analytics', label: 'Analytics Cache', stats: data.cache_stats.analytics, ttlLabel: '5 min' },
        { key: 'seo', label: 'SEO Cache', stats: data.cache_stats.seo, ttlLabel: '1 hr' },
        { key: 'competitor', label: 'Competitor Cache', stats: data.cache_stats.competitor, ttlLabel: '24 hr' },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
            {/* Refresh button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <GlassButton variant="secondary" size="sm" onClick={load} icon={<RefreshCw size={14} />}>
                    Refresh
                </GlassButton>
            </div>

            {/* Cache stats */}
            <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-3)', color: 'var(--text)' }}>
                    <Zap size={16} />
                    <span style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>Response Cache</span>
                </div>
                <div className="audit-stats-grid">
                    {caches.map(({ key, label, stats, ttlLabel }) => (
                        <GlassCard key={key} variant="default" padding="md" className="audit-stat-card">
                            <div className="audit-stat-value">{stats.valid_entries} <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>/ {stats.size}</span></div>
                            <div className="audit-stat-label">{label}</div>
                            <div className="audit-stat-sub">TTL: {ttlLabel} · max {stats.maxsize}</div>
                        </GlassCard>
                    ))}
                </div>
            </div>

            {/* DB pool stats */}
            <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-3)', color: 'var(--text)' }}>
                    <Database size={16} />
                    <span style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>Database Pool</span>
                </div>
                <div className="audit-stats-grid">
                    <GlassCard variant="default" padding="md" className="audit-stat-card">
                        <div className="audit-stat-value">{String(data.database.pool_size)}</div>
                        <div className="audit-stat-label">Pool Size</div>
                        <div className="audit-stat-sub">max persistent connections</div>
                    </GlassCard>
                    <GlassCard variant="default" padding="md" className="audit-stat-card">
                        <div className="audit-stat-value">{String(data.database.checked_out)}</div>
                        <div className="audit-stat-label">Checked Out</div>
                        <div className="audit-stat-sub">active connections</div>
                    </GlassCard>
                </div>
            </div>

            {/* Rate limiter */}
            <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-3)', color: 'var(--text)' }}>
                    <Shield size={16} />
                    <span style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>Rate Limiter</span>
                </div>
                <div className="audit-stats-grid">
                    <GlassCard variant="default" padding="md" className="audit-stat-card">
                        <div className="audit-stat-value">{data.rate_limiter.tracked_ips}</div>
                        <div className="audit-stat-label">Tracked IPs</div>
                        <div className="audit-stat-sub">active in window</div>
                    </GlassCard>
                    <GlassCard variant="default" padding="md" className="audit-stat-card">
                        <div className="audit-stat-value">{data.rate_limiter.limit}</div>
                        <div className="audit-stat-label">Req / Window</div>
                        <div className="audit-stat-sub">{data.rate_limiter.window_seconds}s window</div>
                    </GlassCard>
                </div>
            </div>
        </div>
    );
};


// ── Audit Log sub-component ───────────────────────────────────────────────────

const AuditLogTable: React.FC = () => {
    const [logs, setLogs] = useState<AuditLogEntry[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [actions, setActions] = useState<string[]>([]);
    const [expandedId, setExpandedId] = useState<string | null>(null);

    // Filter state
    const [filterAction, setFilterAction] = useState('');
    const [filterResourceType, setFilterResourceType] = useState('');
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');

    const fetchLogs = useCallback(() => {
        setLoading(true);
        setError(null);
        const params: AuditLogParams = {
            limit: PAGE_SIZE,
            offset: page * PAGE_SIZE,
        };
        if (filterAction) params.action = filterAction;
        if (filterResourceType) params.resource_type = filterResourceType;
        if (filterStartDate) params.start_date = filterStartDate;
        if (filterEndDate) params.end_date = filterEndDate;

        getAuditLogs(params)
            .then(result => {
                setLogs(result.logs);
                setTotal(result.total);
            })
            .catch(() => setError('Failed to load audit logs'))
            .finally(() => setLoading(false));
    }, [page, filterAction, filterResourceType, filterStartDate, filterEndDate]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    useEffect(() => {
        getAuditLogActions().then(setActions).catch(() => {});
    }, []);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const handleApplyFilters = () => {
        setPage(0);
        fetchLogs();
    };

    const actionOptions = [
        { value: '', label: 'All actions' },
        ...actions.map(a => ({ value: a, label: a })),
    ];

    const resourceTypeOptions = [
        { value: '', label: 'All resources' },
        { value: 'user', label: 'user' },
        { value: 'campaign', label: 'campaign' },
        { value: 'lead_capture_form', label: 'lead_capture_form' },
        { value: 'content', label: 'content' },
    ];

    return (
        <div className="audit-log-section">
            {/* Filter bar */}
            <GlassCard variant="default" padding="md" className="audit-filter-bar">
                <div className="audit-filters-row">
                    <GlassSelect
                        label="Action"
                        options={actionOptions}
                        value={filterAction}
                        onChange={setFilterAction}
                    />
                    <GlassSelect
                        label="Resource type"
                        options={resourceTypeOptions}
                        value={filterResourceType}
                        onChange={setFilterResourceType}
                    />
                    <GlassInput
                        label="From date"
                        type="date"
                        value={filterStartDate}
                        onChange={setFilterStartDate}
                    />
                    <GlassInput
                        label="To date"
                        type="date"
                        value={filterEndDate}
                        onChange={setFilterEndDate}
                    />
                    <div className="audit-filter-actions">
                        <GlassButton variant="primary" size="sm" onClick={handleApplyFilters} icon={<RefreshCw size={14} />}>
                            Apply
                        </GlassButton>
                        <GlassButton
                            variant="secondary"
                            size="sm"
                            onClick={() => downloadCsv(logs)}
                            icon={<Download size={14} />}
                            disabled={logs.length === 0}
                        >
                            Export CSV
                        </GlassButton>
                    </div>
                </div>
            </GlassCard>

            {/* Table */}
            {loading ? (
                <div style={{ color: 'var(--text-secondary)', padding: 'var(--space-4)' }}>Loading…</div>
            ) : error ? (
                <div style={{ color: 'var(--danger)', padding: 'var(--space-4)' }}>{error}</div>
            ) : (
                <GlassCard variant="default" padding="none" className="audit-table-card">
                    <table className="audit-table">
                        <thead>
                            <tr>
                                <th>Timestamp</th>
                                <th>User</th>
                                <th>Action</th>
                                <th>Resource</th>
                                <th>IP</th>
                                <th>Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.length === 0 ? (
                                <tr>
                                    <td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 'var(--space-6)' }}>
                                        No audit log entries found
                                    </td>
                                </tr>
                            ) : logs.map(log => (
                                <React.Fragment key={log.log_id}>
                                    <tr
                                        className={`audit-row${expandedId === log.log_id ? ' audit-row--expanded' : ''}`}
                                        onClick={() => setExpandedId(expandedId === log.log_id ? null : log.log_id)}
                                    >
                                        <td className="audit-cell-ts">{formatDate(log.created_at)}</td>
                                        <td className="audit-cell-user">{log.user_email ?? <span style={{ color: 'var(--text-secondary)' }}>anonymous</span>}</td>
                                        <td className="audit-cell-action">
                                            <GlassBadge variant={actionVariant(log.action)}>
                                                {log.action ?? '—'}
                                            </GlassBadge>
                                        </td>
                                        <td className="audit-cell-resource">
                                            {log.resource_name && <span>{log.resource_name}</span>}
                                            {log.resource_type && <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)' }}> ({log.resource_type})</span>}
                                        </td>
                                        <td className="audit-cell-ip" style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)' }}>{log.ip_address ?? '—'}</td>
                                        <td className="audit-cell-details">
                                            {log.details && <span style={{ color: 'var(--primary-500)', fontSize: 'var(--text-xs)', cursor: 'pointer' }}>expand</span>}
                                        </td>
                                    </tr>
                                    {expandedId === log.log_id && log.details && (
                                        <tr className="audit-row-details">
                                            <td colSpan={6}>
                                                <pre className="audit-details-pre">
                                                    {JSON.stringify(log.details, null, 2)}
                                                </pre>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </GlassCard>
            )}

            {/* Pagination */}
            <div className="audit-pagination">
                <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
                    {total} total entries — page {page + 1} of {totalPages}
                </span>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    <GlassButton
                        variant="secondary"
                        size="sm"
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                        icon={<ChevronLeft size={14} />}
                    >
                        Prev
                    </GlassButton>
                    <GlassButton
                        variant="secondary"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                        disabled={page >= totalPages - 1}
                        icon={<ChevronRight size={14} />}
                    >
                        Next
                    </GlassButton>
                </div>
            </div>
        </div>
    );
};

// ── Main AuditLogViewer component ─────────────────────────────────────────────

const TABS = [
    { id: 'stats', label: 'Platform Stats' },
    { id: 'audit', label: 'Audit Log' },
    { id: 'performance', label: 'Performance' },
];

const AuditLogViewer: React.FC = () => {
    const [activeTab, setActiveTab] = useState('stats');

    return (
        <div className="audit-log-viewer">
            <GlassTabs tabs={TABS} activeTab={activeTab} onChange={setActiveTab} />

            <div className="audit-tab-content">
                {activeTab === 'stats' && <PlatformStats />}
                {activeTab === 'audit' && <AuditLogTable />}
                {activeTab === 'performance' && <PerformancePanel />}
            </div>
        </div>
    );
};

export default AuditLogViewer;
